/* eslint-disable */
/**
 * OSDashboardNy.jsx — Nyt OS-admin-dashboard (Fase 1)
 *
 * Feature-flagget bag os.tilstand['ny_os_dashboard_enabled'].
 * Når flag = false: OSDashboard.jsx vises uændret.
 * Når flag = true:  Denne komponent vises i stedet.
 *
 * IA: 5 sektioner
 *   OVERSIGT        — Kommandocentralen (landing)
 *   FORRETNING      — Firma / Økonomi / BI / Sager
 *   OS SYSTEM       — OS Oversigt / Hjerne / Vækst / Afdelinger / Orchestrator
 *   ARBEJDSPLADS    — Indbakke / Idéer & Projekter / Teamnotater / OS Chat / Chief of Staff
 *   UDAD            — Hjemmeside / Analytics / Scheduler / Infoskærm
 *
 * Genbruger alle eksisterende sub-komponenter — ingen omskrivning.
 * Fjernet: SoMe-fane (hardcodet falsk data), Meta Ads-placeholder.
 *
 * Brug:
 *   import OSDashboardNy from './OSDashboardNy';
 *   // Renderes fra App.js via feature-flag-logik
 */

import React, { useState, useEffect, useCallback, useRef } from "react";
import { Home } from "lucide-react";
import { SubBoundary } from "./components/Ui";

// ── Nye Fase 0/1/2 paneler ────────────────────────────────
import SagerPanel        from "./os-ny/paneler/SagerPanel";
import HjemmesidePanel   from "./os-ny/paneler/HjemmesidePanel";
import HjernePanel       from "./os-ny/paneler/HjernePanel";
import OekonomiPanel     from "./os-ny/paneler/OekonomiPanel";
import AgenterPanel      from "./os-ny/paneler/AgenterPanel";
import AfdelingerPanel   from "./os-ny/paneler/AfdelingerPanel";
// ── Fase 3 — manglende dashboards ─────────────────────────
import SchedulerPanel    from "./os-ny/paneler/SchedulerPanel";
import OsOversigt2Panel  from "./os-ny/paneler/OsOversigt2Panel";
import AnalyticsPanel    from "./os-ny/paneler/AnalyticsPanel";
// ── Fase 4 — beregner-data ─────────────────────────────────
import BeregnerDataPanel from "./os-ny/paneler/BeregnerDataPanel";
// ── Fase 5 — Investering & Vækst panels ─────────────────────
import RevenueVelocityPanel           from "./RevenueVelocityPanel";
import KlientvaekstScenariePanelIV    from "./KlientvaekstScenariePanelIV";
import ScenariesimulatoriVPanel       from "./ScenariesimulatoriVPanel";
import ChurnReaktiveringPanelIV       from "./ChurnReaktiveringPanelIV";
import BeregnerHeatmapPanelIV         from "./BeregnerHeatmapPanelIV";
// ── Fase 5 — Analyse Extra panels ───────────────────────────
import FaseFunnelAnalysePanel             from "./FaseFunnelAnalysePanel";
import BeregnerKlientPipelineAnalysePanel from "./BeregnerKlientPipelineAnalysePanel";
// -- Fase 5 -- Data Science panels ------------------------------------------
import MRRForecastDSPanel          from "./os-ny/paneler/ds/MRRForecastDSPanel";
import BeregnerSegmenteringDSPanel from "./os-ny/paneler/ds/BeregnerSegmenteringDSPanel";
import AnomalidedetektionDSPanel   from "./os-ny/paneler/ds/AnomalidedetektionDSPanel";
import SagstidsPrognoseDSPanel     from "./os-ny/paneler/ds/SagstidsPrognoseDSPanel";
// ── Fase 5 — Business Intelligence panels ────────────────────
import MRRKurveBIPanel         from "./os-ny/paneler/bi/MRRKurveBIPanel";
import SagFunnelBIPanel        from "./os-ny/paneler/bi/SagFunnelBIPanel";
import CohortRetentionBIPanel  from "./os-ny/paneler/bi/CohortRetentionBIPanel";
import AktivitetsHeatmapBIPanel from "./os-ny/paneler/bi/AktivitetsHeatmapBIPanel";
// ── Fase 5 — Strategisk Raadgiver ──────────────────────────
import MRRScenariePanelSR    from "./os-ny/paneler/MRRScenariePanelSR";
import KlientPipelineFunnelSR from "./os-ny/paneler/KlientPipelineFunnelSR";
import FinansielSundhedSR     from "./os-ny/paneler/FinansielSundhedSR";
import SEOMomentumSR          from "./os-ny/paneler/SEOMomentumSR";
import SagstidsTrendSR        from "./os-ny/paneler/SagstidsTrendSR";
// ── Gruppe 1 — Sags-diagnostik paneler ────────────────────
import StagnationsradarPanel      from "./os-ny/paneler/StagnationsradarPanel";
import DeadlineKommandocenterPanel from "./os-ny/paneler/DeadlineKommandocenterPanel";
import AktivitetsscorePanel       from "./os-ny/paneler/AktivitetsscorePanel";
import SagsbehandlerWorkloadPanel  from "./os-ny/paneler/SagsbehandlerWorkloadPanel";
import FuldmagtCompliancePanel     from "./os-ny/paneler/FuldmagtCompliancePanel";
import OSCockpit    from "./os-ny/OSCockpit";
import TopBjaelke   from "./os-ny/komponenter/TopBjaelke";

// ── Eksisterende sub-komponenter (uændrede) ───────────────
import FirmaOverblik from "./FirmaOverblik";
import ChiefOfStaff from "./ChiefOfStaff";
import OSInfoskaerm from "./OSInfoskaerm";
import OSWebsiteIntelligence from "./OSWebsiteIntelligence";
import HjerneDashboard from "./HjerneDashboard";
import HjerneVaekst from "./HjerneVaekst";
import BIDashboard from "./BIDashboard";
import OSOpgaveChatDashboard from "./OSOpgaveChatDashboard";
import OSIndbakke from "./OSIndbakke";
import OSIdeePipeline from "./OSIdeePipeline";
import OSTeamNoter from "./OSTeamNoter";
import OrchestratorDashboard from "./OrchestratorDashboard";

// ─── API helper ───────────────────────────────────────────
const api = async (sti, metode = 'GET', data = null) => {
  const res = await fetch('/api' + sti, {
    method: metode,
    credentials: 'include',
    headers: { 'Content-Type': 'application/json' },
    body: data ? JSON.stringify(data) : null,
  });
  if (!res.ok) {
    const err = await res.json().catch(() => ({}));
    throw new Error(err.fejl || `HTTP ${res.status}`);
  }
  return res.json();
};

// ─── Design tokens (identisk med OSDashboard.jsx) ─────────
const C = {
  sidebar: '#060d1a',
  sidebarTxt: '#9db3c8',
  sidebarAkt: '#3fd4c7',
  bg: '#060917',
  kort: 'rgba(8,12,30,0.60)',
  kant: 'rgba(255,255,255,0.08)',
  tekst: '#e2e8f0',
  subtekst: '#7a8fa6',
  success: '#22c55e',
  warning: '#f59e0b',
  danger: '#ef4444',
  info: '#3b82f6',
  lilla: '#8b5cf6',
};

// ─── Genbrugelige UI-primitiver ───────────────────────────
const Kort = ({ children, style = {}, ...rest }) => (
  <div style={{
    background: 'rgba(8,12,30,0.60)', border: '1px solid rgba(255,255,255,0.08)', borderRadius: 12,
    backdropFilter: 'blur(16px)', WebkitBackdropFilter: 'blur(16px)',
    padding: 20, ...style
  }} {...rest}>
    {children}
  </div>
);

const StatKort = ({ label, value, farve = C.info, ikon, onClick, trend, badge }) => {
  const safeTrend = (trend != null && isFinite(trend)) ? trend : null;
  const trendFarve = safeTrend > 0 ? C.success : safeTrend < 0 ? C.danger : C.subtekst;
  const trendPil = safeTrend > 0 ? '↑' : safeTrend < 0 ? '↓' : null;
  return (
    <Kort
      style={{ flex: 1, minWidth: 160, cursor: onClick ? 'pointer' : 'default', transition: 'transform 0.15s, box-shadow 0.15s' }}
      onClick={onClick}
      onMouseEnter={onClick ? (e) => { e.currentTarget.style.transform = 'translateY(-2px)'; e.currentTarget.style.boxShadow = '0 8px 24px rgba(35,34,190,0.18)'; } : undefined}
      onMouseLeave={onClick ? (e) => { e.currentTarget.style.transform = ''; e.currentTarget.style.boxShadow = ''; } : undefined}
    >
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 8 }}>
        <span style={{ fontSize: 20 }}>{ikon}</span>
        <span style={{ fontSize: 13, color: C.subtekst, fontWeight: 600, letterSpacing: 0.3 }}>{label}</span>
        {badge && (
          <span style={{
            marginLeft: 'auto', fontSize: 9, fontWeight: 700, padding: '2px 6px', borderRadius: 4,
            background: 'rgba(59,130,246,0.15)', color: C.info, border: '1px solid rgba(59,130,246,0.3)',
            textTransform: 'uppercase',
          }}>{badge}</span>
        )}
        {onClick && !badge && <span style={{ marginLeft: 'auto', fontSize: 11, color: C.subtekst, opacity: 0.6 }}>↓</span>}
      </div>
      <div style={{ display: 'flex', alignItems: 'baseline', gap: 8 }}>
        <div style={{ fontSize: 32, fontWeight: 700, color: farve }}>{value ?? '—'}</div>
        {trendPil && (
          <span style={{ fontSize: 13, fontWeight: 700, color: trendFarve }}>
            {trendPil}{Math.abs(Math.round(safeTrend))}
          </span>
        )}
      </div>
    </Kort>
  );
};

const Dot = ({ aktiv, farve }) => (
  <span style={{
    display: 'inline-block', width: 10, height: 10, borderRadius: '50%',
    background: farve || (aktiv ? C.success : '#94a3b8'), marginRight: 8,
    boxShadow: aktiv ? `0 0 6px ${farve || C.success}` : 'none',
  }} />
);

const Knap = ({ children, onClick, farve = C.info, lille = false, disabled = false, style = {} }) => (
  <button onClick={onClick} disabled={disabled} style={{
    background: farve, color: '#fff', border: 'none', borderRadius: 7,
    padding: lille ? '4px 12px' : '8px 18px',
    fontSize: lille ? 12 : 14, fontWeight: 600, cursor: disabled ? 'not-allowed' : 'pointer',
    opacity: disabled ? 0.45 : 1, transition: 'opacity .15s', ...style,
  }}
    onMouseEnter={e => { if (!disabled) e.currentTarget.style.opacity = '.82'; }}
    onMouseLeave={e => { if (!disabled) e.currentTarget.style.opacity = '1'; }}
  >
    {children}
  </button>
);

// ─── TomTilstand — vises når data mangler integration ─────
/**
 * Genbrugelig komponent der signalerer manglende tilslutning
 * uden at vise 0-tal eller hardcodede værdier.
 *
 * Props:
 *   titel       — kort beskrivelse, fx "Facebook API"
 *   besked      — hvad der skal til, fx "Kræver Meta API-token i .env"
 *   ikon        — valgfrit emoji/tekst ikon
 *   badge       — LIVE | KONFIGURERET | IKKE FORBUNDET
 */
export function TomTilstand({ titel, besked, ikon = 'plug', badge = 'IKKE FORBUNDET', style = {} }) {
  const badgeFarver = {
    'LIVE': { bg: 'rgba(34,197,94,0.12)', kant: 'rgba(34,197,94,0.3)', txt: '#4ade80' },
    'KONFIGURERET': { bg: 'rgba(59,130,246,0.12)', kant: 'rgba(59,130,246,0.3)', txt: '#60a5fa' },
    'IKKE FORBUNDET': { bg: 'rgba(100,116,139,0.10)', kant: 'rgba(100,116,139,0.25)', txt: '#94a3b8' },
  };
  const bf = badgeFarver[badge] || badgeFarver['IKKE FORBUNDET'];
  return (
    <div style={{
      display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
      padding: '32px 20px', textAlign: 'center', gap: 12,
      background: bf.bg, border: `1px solid ${bf.kant}`, borderRadius: 12,
      ...style,
    }}>
      <span style={{ fontSize: 28, opacity: 0.5 }}>{ikon}</span>
      <div>
        <div style={{ fontSize: 14, fontWeight: 600, color: C.tekst, marginBottom: 4 }}>{titel}</div>
        {besked && <div style={{ fontSize: 12, color: C.subtekst, lineHeight: 1.5, maxWidth: 320 }}>{besked}</div>}
      </div>
      <span style={{
        fontSize: 10, fontWeight: 700, padding: '3px 10px', borderRadius: 10,
        background: bf.bg, border: `1px solid ${bf.kant}`, color: bf.txt,
        textTransform: 'uppercase', letterSpacing: 0.8,
      }}>{badge}</span>
    </div>
  );
}

// ─── Mobil-detektion ──────────────────────────────────────
const MobilCtx = React.createContext(false);
function useMobile() {
  const [m, setM] = React.useState(window.innerWidth < 768);
  React.useEffect(() => {
    const h = () => setM(window.innerWidth < 768);
    window.addEventListener('resize', h);
    return () => window.removeEventListener('resize', h);
  }, []);
  return m;
}

// ─── Linkify ──────────────────────────────────────────────
const URL_REGEX = /(https?:\/\/[^\s<>"']+)/g;
function LinkifyTekst({ tekst, style }) {
  if (!tekst) return null;
  const dele = tekst.split(URL_REGEX);
  return (
    <span style={style}>
      {dele.map((del, i) =>
        URL_REGEX.test(del) ? (
          <a key={i} href={del} target="_blank" rel="noopener noreferrer"
            style={{ color: '#3fd4c7', textDecoration: 'underline', wordBreak: 'break-all' }}
            onClick={e => e.stopPropagation()}>{del}</a>
        ) : del
      )}
    </span>
  );
}

// ─── NY NAVIGATION STRUKTUR ───────────────────────────────
const NY_NAV_GROUPS = [
  {
    label: 'OVERSIGT',
    items: [
      { id: 'oversigt', label: 'Oversigt', ikon: '⬡' },
    ],
  },
  {
    label: 'FORRETNING',
    items: [
      { id: 'firma', label: 'Firma', ikon: '◈' },
      { id: 'okonomi', label: 'Økonomi', ikon: '◎' },
      { id: 'bi', label: 'BI Dashboard', ikon: '◇' },
      { id: 'sager', label: 'Sager', ikon: '▦' },
      { id: 'beregner_data', label: 'Beregner-data', ikon: '◫' },
    ],
  },
  {
    label: 'SAGS-DIAGNOSTIK',
    items: [
      { id: 'stagnationsradar',        label: 'Stagnationsradar',   ikon: '◎' },
      { id: 'deadline_kommandocenter', label: 'Deadline-center',    ikon: '◈' },
      { id: 'aktivitetsscore',         label: 'Aktivitetsscore',    ikon: '◗' },
      { id: 'sagsbehandler_workload',  label: 'Workload',           ikon: '▣' },
      { id: 'fuldmagt_compliance',     label: 'Fuldmagt',           ikon: '◆' },
    ],
  },
  {
    label: 'INVESTERING & VÆKST',
    items: [
      { id: 'iv_revenue_velocity',      label: 'Revenue Velocity',     ikon: '◎' },
      { id: 'iv_klientvaekst_scenarie', label: 'Klientvækst & Scenarier', ikon: '◈' },
      { id: 'iv_scenarie_simulator',    label: 'Scenarie-Simulator',   ikon: '◐' },
      { id: 'iv_churn_reaktivering',    label: 'Churn & Re-aktivering', ikon: '◑' },
      { id: 'iv_beregner_heatmap',      label: 'Beregner Heatmap',     ikon: '▦' },
    ],
  },
  {
    label: 'ANALYSE EXTRA',
    items: [
      { id: 'a_fasefunnel',        label: 'Fasefunnel',         ikon: '◇' },
      { id: 'a_beregner_pipeline', label: 'Beregner Pipeline',  ikon: '◆' },
    ],
  },
  {
    label: 'DATA SCIENCE',
    items: [
      { id: 'ds_mrr_forecast',          label: 'MRR Forecast',       ikon: '◈' },
      { id: 'ds_beregner_segmentering', label: 'Beregner-segment',   ikon: '◎' },
      { id: 'ds_anomali_detektion',     label: 'Anomalidetektion',   ikon: '◉' },
      { id: 'ds_sagstid_prognose',      label: 'Sagstidsprognose',   ikon: '◇' },
    ],
  },
  {
    label: 'BUSINESS INTELLIGENCE',
    items: [
      { id: 'bi_mrr',     label: 'MRR-kurve',         ikon: '◈' },
      { id: 'bi_funnel',  label: 'Sag-Funnel',         ikon: '◇' },
      { id: 'bi_cohort',  label: 'Cohort Retention',   ikon: '◎' },
      { id: 'bi_heatmap', label: 'Aktivitetsheatmap',  ikon: '▦' },
    ],
  },
  {
    label: 'STRATEGISK RAADGIVER',
    items: [
      { id: 'sr_mrr',       label: 'MRR og Scenarier',   ikon: '◈' },
      { id: 'sr_pipeline',  label: 'Klient Pipeline',    ikon: '◐' },
      { id: 'sr_finansiel', label: 'Finansiel Sundhed',  ikon: '◎' },
      { id: 'sr_seo',       label: 'SEO-Momentum',       ikon: '◻' },
      { id: 'sr_sagstid',   label: 'Sagstids-Trend',     ikon: '▦' },
    ],
  },
  {
    label: 'OS SYSTEM',
    items: [
      { id: 'os_oversigt', label: 'OS Oversigt', ikon: '▣' },
      { id: 'hjerne', label: 'Hjerne', ikon: '◉' },
      { id: 'vaekst', label: 'Vækst', ikon: '◈' },
      { id: 'afdelinger', label: 'Afdelinger', ikon: '▥' },
      { id: 'orchestrator', label: 'Orchestrator', ikon: '◆' },
    ],
  },
  {
    label: 'ARBEJDSPLADS',
    items: [
      { id: 'indbakke', label: 'Indbakke', ikon: '▤' },
      { id: 'ideer_projekter', label: 'Idéer & Projekter', ikon: '◐' },
      { id: 'teamnoter', label: 'Teamnotater', ikon: '▧' },
      { id: 'chat', label: 'Opgave Chat', ikon: '💬' },
      { id: 'chief', label: 'Chief of Staff', ikon: '◍' },
    ],
  },
  {
    label: 'UDAD',
    items: [
      { id: 'hjemmeside', label: 'Hjemmeside', ikon: '◻' },
      { id: 'analytics', label: 'Analytics', ikon: '◈' },
      { id: 'scheduler', label: 'Scheduler', ikon: '◷' },
      { id: 'infoskaerm', label: 'Infoskærm', ikon: '◫' },
    ],
  },
];

// ─── IDEER & PROJEKTER FUSIONERET ────────────────────────
// Intern fane-switch: ideer | projekter
function IdeerProjekterFusioneret() {
  const [intern, setIntern] = useState('ideer');
  return (
    <div>
      <div style={{ display: 'flex', gap: 8, marginBottom: 20 }}>
        {[{ id: 'ideer', label: 'Idéer' }, { id: 'projekter', label: 'Projekter' }].map(t => (
          <button key={t.id} onClick={() => setIntern(t.id)} style={{
            background: intern === t.id ? C.sidebarAkt : 'rgba(255,255,255,0.06)',
            color: intern === t.id ? '#fff' : C.sidebarTxt,
            border: 'none', borderRadius: 8, padding: '6px 18px',
            fontSize: 13, fontWeight: 700, cursor: 'pointer',
          }}>
            {t.label}
          </button>
        ))}
      </div>
      <OSIdeePipeline key={intern} defaultFane={intern} />
    </div>
  );
}

// ─── DASHBOARD-MAP ────────────────────────────────────────
// os_oversigt er den gamle OversigtDashboard — importeres inline nedenfor
// (undgår cirkulær dep; vi definerer den her som lazy reference)
const DASHBOARD_MAP = {
  firma: FirmaOverblik,
  bi: BIDashboard,
  chief: ChiefOfStaff,
  hjerne: HjernePanel,    // nyt panel bag ny_os_dashboard_enabled
  vaekst: HjerneVaekst,
  chat: OSOpgaveChatDashboard,
  indbakke: OSIndbakke,
  ideer_projekter: IdeerProjekterFusioneret,
  teamnoter: OSTeamNoter,
  orchestrator: OrchestratorDashboard,
  infoskaerm: () => <OSInfoskaerm inline={true} />,
  // Fase 1 — nye selvstændige paneler
  sager: SagerPanel,
  hjemmeside: HjemmesidePanel,
  hjerne_ny: HjernePanel,
  // Fase 2 — Økonomi, Agenter, Afdelinger
  okonomi:    OekonomiPanel,
  agenter:    AgenterPanel,
  afdelinger: AfdelingerPanel,
  // Fase 3 — manglende dashboards
  scheduler:    SchedulerPanel,    // erstatter legacy LegacyWrapper(scheduler)
  os_oversigt:  OsOversigt2Panel,  // erstatter LegacyOsOversigt
  analytics:    AnalyticsPanel,    // erstatter OSWebsiteIntelligence
  // Fase 4 — beregner-data statistik
  beregner_data: BeregnerDataPanel,
  // Fase 5 — Investering & Vækst
  iv_revenue_velocity:        RevenueVelocityPanel,
  iv_klientvaekst_scenarie:   KlientvaekstScenariePanelIV,
  iv_scenarie_simulator:      ScenariesimulatoriVPanel,
  iv_churn_reaktivering:      ChurnReaktiveringPanelIV,
  iv_beregner_heatmap:        BeregnerHeatmapPanelIV,
  // Fase 5 — Analyse Extra
  a_fasefunnel:               FaseFunnelAnalysePanel,
  a_beregner_pipeline:        BeregnerKlientPipelineAnalysePanel,
  // Fase 5 — Data Science
  ds_mrr_forecast:           MRRForecastDSPanel,
  ds_beregner_segmentering:  BeregnerSegmenteringDSPanel,
  ds_anomali_detektion:      AnomalidedetektionDSPanel,
  ds_sagstid_prognose:       SagstidsPrognoseDSPanel,
  // Fase 5 — Business Intelligence
  bi_mrr:     MRRKurveBIPanel,
  bi_funnel:  SagFunnelBIPanel,
  bi_cohort:  CohortRetentionBIPanel,
  bi_heatmap: AktivitetsHeatmapBIPanel,
  // Fase 5 - Strategisk Raadgiver panels
  sr_mrr:       MRRScenariePanelSR,
  sr_pipeline:  KlientPipelineFunnelSR,
  sr_finansiel: FinansielSundhedSR,
  sr_seo:       SEOMomentumSR,
  sr_sagstid:   SagstidsTrendSR,
  // Fase 5 — Sags-diagnostik
  stagnationsradar:          StagnationsradarPanel,
  deadline_kommandocenter:   DeadlineKommandocenterPanel,
  aktivitetsscore:           AktivitetsscorePanel,
  sagsbehandler_workload:    SagsbehandlerWorkloadPanel,
  fuldmagt_compliance:       FuldmagtCompliancePanel,
};
// oversigt defineres som inline-funktion nedenfor i denne fil.

// ─────────────────────────────────────────────────────────
// OVERSIGT — Kommandocentral (ny landing)
// ─────────────────────────────────────────────────────────
function NyOversigtDashboard({ onNavigere }) {
  const mobil = React.useContext(MobilCtx);
  const [stat, setStat] = useState(null);
  const [internStat, setInternStat] = useState(null);
  const [beslutninger, setBeslutninger] = useState([]);
  const [afventerOpgaver, setAfventerOpgaver] = useState([]);
  const [agentSamtaler, setAgentSamtaler] = useState([]);
  const [deployHistorik, setDeployHistorik] = useState(null);
  const [hjernePuls, setHjernePuls] = useState(null); // { oprettet_kl, minutter_siden }
  const [cashflowTilstand, setCashflowTilstand] = useState(null);
  const [loader, setLoader] = useState(true);
  const [modificer, setModificer] = useState({ id: null, kommentar: '' });

  const hent = useCallback(async () => {
    try {
      const [s, is, b, ao, ak, dh, tilstand] = await Promise.allSettled([
        api('/os/statistik'),
        api('/os/intern/statistik'),
        api('/os/beslutninger'),
        api('/os/opgaver?status=AFVENTER&limit=10'),
        api('/os/hjerne/kommunikation?limit=8'),
        api('/os/hjerne/deploy-historik?limit=5'),
        api('/os/tilstand'),
      ]);
      if (s.status === 'fulfilled') setStat(s.value);
      if (is.status === 'fulfilled') setInternStat(is.value);
      if (b.status === 'fulfilled') setBeslutninger(Array.isArray(b.value) ? b.value : (b.value?.beslutninger || []));
      if (ao.status === 'fulfilled') setAfventerOpgaver(Array.isArray(ao.value) ? ao.value : (ao.value?.opgaver || []));
      if (ak.status === 'fulfilled') setAgentSamtaler(ak.value?.kommunikation || []);
      if (dh.status === 'fulfilled') setDeployHistorik(dh.value);
      if (tilstand.status === 'fulfilled') {
        const t = tilstand.value;
        const entries = Array.isArray(t) ? t : (t?.tilstand || []);
        setCashflowTilstand(entries);
      }
    } finally {
      setLoader(false);
    }
  }, []);

  // Hent seneste hjerne-viden-skriv
  useEffect(() => {
    api('/os/hjerne/viden/seneste')
      .then(d => {
        if (d?.oprettet_kl) {
          const diffMin = Math.round((Date.now() - new Date(d.oprettet_kl)) / 60000);
          setHjernePuls({ oprettet_kl: d.oprettet_kl, minutter_siden: diffMin });
        }
      })
      .catch(() => setHjernePuls(null));
  }, []);

  useEffect(() => {
    hent();
    const id = setInterval(hent, 30000);
    return () => clearInterval(id);
  }, [hent]);

  const svar = async (id, svar_val, kommentar = '') => {
    try {
      await api(`/os/beslutninger/${id}/svar`, 'POST', { handling: svar_val, kommentar });
      const opdateret = await api('/os/beslutninger');
      setBeslutninger(Array.isArray(opdateret) ? opdateret : (opdateret?.beslutninger || []));
      setModificer({ id: null, kommentar: '' });
    } catch (err) {
      console.error('[NyOversigt] svar fejl:', err);
    }
  };

  // System-status bjælke
  const kritiskeFejl = stat?.kritiske_fejl_24h ?? 0;
  const systemOk = stat != null && kritiskeFejl === 0 && beslutninger.length === 0;
  const systemAdvarsel = beslutninger.length > 0 && kritiskeFejl === 0;
  const systemStatus = kritiskeFejl > 0 ? 'KRITISK' : systemAdvarsel ? 'AFVENTER' : 'OK';
  const systemFarveMap = {
    'OK': { bg: 'rgba(34,197,94,0.10)', kant: 'rgba(34,197,94,0.25)', txt: '#4ade80' },
    'AFVENTER': { bg: 'rgba(245,158,11,0.10)', kant: 'rgba(245,158,11,0.25)', txt: '#fbbf24' },
    'KRITISK': { bg: 'rgba(239,68,68,0.14)', kant: 'rgba(239,68,68,0.35)', txt: '#f87171' },
  };
  const sf = systemFarveMap[systemStatus] || systemFarveMap['OK'];

  const formatTid = (iso) => {
    if (!iso) return '—';
    return new Date(iso).toLocaleString('da-DK', { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' });
  };

  const hjernePulsLabel = () => {
    if (!hjernePuls) return null;
    const min = hjernePuls.minutter_siden;
    if (min < 5) return 'Nu lige';
    if (min < 60) return `${min} min siden`;
    if (min < 1440) return `${Math.round(min / 60)} timer siden`;
    return `${Math.round(min / 1440)} dage siden`;
  };

  const loenDaekket = cashflowTilstand?.find(t => t.noegel === 'loen_forpligtelse_daekket')?.vaerdi;

  if (loader) return <div style={{ padding: 40, color: C.subtekst }}>Henter data…</div>;

  return (
    <div>
      {/* Header */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 12, marginBottom: 18 }}>
        <div>
          <h2 style={{ margin: 0, color: C.tekst, fontSize: 22, fontWeight: 800 }}>Kommandocentral</h2>
          <div style={{ fontSize: 12, color: C.subtekst, marginTop: 3 }}>
            {new Date().toLocaleDateString('da-DK', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' })}
          </div>
        </div>
        {/* System-status bjælke */}
        <div style={{
          display: 'flex', alignItems: 'center', gap: 8,
          background: sf.bg, border: `1px solid ${sf.kant}`,
          borderRadius: 20, padding: '6px 16px', fontSize: 13, fontWeight: 700,
          color: sf.txt,
        }}>
          <Dot aktiv={systemStatus === 'OK'} farve={sf.txt} />
          System: {systemStatus}
          {kritiskeFejl > 0 && ` · ${kritiskeFejl} kritisk${kritiskeFejl > 1 ? 'e' : ''}`}
        </div>
      </div>

      {/* Afventende beslutninger — ALTID øverst, fuldt synlige */}
      {beslutninger.length > 0 && (
        <Kort style={{ marginBottom: 20, borderTop: '3px solid rgba(245,158,11,0.6)', borderLeft: '3px solid rgba(245,158,11,0.3)' }}>
          <h3 style={{ margin: '0 0 16px', fontSize: 16, color: '#f59e0b', display: 'flex', alignItems: 'center', gap: 8 }}>
            Afventende beslutninger
            <span style={{ fontSize: 12, fontWeight: 700, background: 'rgba(245,158,11,0.15)', border: '1px solid rgba(245,158,11,0.3)', borderRadius: 10, padding: '1px 8px', color: '#f59e0b' }}>
              {beslutninger.length}
            </span>
          </h3>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
            {beslutninger.map(b => (
              <div key={b.id} style={{ background: 'rgba(245,158,11,0.08)', border: '1px solid rgba(245,158,11,0.22)', borderRadius: 8, padding: '12px 14px' }}>
                <p style={{ margin: '0 0 6px', fontSize: 15, color: C.tekst, fontWeight: 700 }}>{b.titel || 'Beslutning'}</p>
                {b.beskrivelse && <p style={{ margin: '0 0 8px', fontSize: 13, color: C.subtekst, whiteSpace: 'pre-wrap' }}><LinkifyTekst tekst={b.beskrivelse} /></p>}
                {b.konsekvens && (
                  <div style={{ marginBottom: 8, padding: '6px 10px', background: 'rgba(245,158,11,0.07)', border: '1px solid rgba(245,158,11,0.2)', borderRadius: 6 }}>
                    <span style={{ fontSize: 11, fontWeight: 700, color: '#f59e0b', textTransform: 'uppercase', marginRight: 6 }}>Konsekvens:</span>
                    <span style={{ fontSize: 12, color: C.tekst }}>{b.konsekvens}</span>
                  </div>
                )}
                <div style={{ display: 'flex', gap: 8 }}>
                  <Knap farve={C.success} lille onClick={() => svar(b.id, 'GODKENDT')}>GODKEND</Knap>
                  <Knap farve='#f39c12' lille onClick={() => setModificer({ id: b.id, kommentar: '' })}>MODIFICER</Knap>
                  <Knap farve={C.danger} lille onClick={() => svar(b.id, 'AFVIST')}>AFVIS</Knap>
                </div>
                {modificer.id === b.id && (
                  <div style={{ marginTop: 10 }}>
                    <textarea
                      value={modificer.kommentar}
                      onChange={e => setModificer(m => ({ ...m, kommentar: e.target.value }))}
                      placeholder="Beskriv hvad der skal ændres (påkrævet)…"
                      rows={2}
                      style={{ width: '100%', boxSizing: 'border-box', background: 'rgba(255,255,255,0.06)', border: '1px solid rgba(243,156,18,0.4)', borderRadius: 6, color: '#e8eaf6', padding: '7px 10px', fontSize: 13, resize: 'none' }}
                    />
                    <div style={{ display: 'flex', gap: 8, marginTop: 6 }}>
                      <Knap farve='#f39c12' lille disabled={!modificer.kommentar.trim()} onClick={() => svar(b.id, 'MODIFICERET', modificer.kommentar)}>SEND</Knap>
                      <Knap farve={C.subtekst} lille onClick={() => setModificer({ id: null, kommentar: '' })}>ANNULLER</Knap>
                    </div>
                  </div>
                )}
              </div>
            ))}
          </div>
        </Kort>
      )}

      {/* KPI-række */}
      <div style={{ display: 'flex', gap: 14, flexWrap: 'wrap', marginBottom: 24 }}>
        <StatKort label="Aktive sager" value={stat?.aktive_sager ?? internStat?.aktive_sager} ikon="▦" farve={C.info} onClick={() => onNavigere && onNavigere('sager')} />
        <StatKort label="Klienter" value={internStat?.total_klienter ?? '—'} ikon="◈" farve={C.success} />
        <StatKort label="OS opgaver (7d)" value={stat?.os_opgaver_7d ?? stat?.opgaver_7d ?? '—'} ikon="▣" farve={C.lilla} onClick={() => onNavigere && onNavigere('os_oversigt')} />
        <StatKort label="Afventende beslutninger" value={beslutninger.length} ikon="◎" farve={beslutninger.length > 0 ? C.warning : C.success} onClick={beslutninger.length > 0 ? () => document.getElementById('ny-beslutninger')?.scrollIntoView({ behavior: 'smooth' }) : undefined} />
        <StatKort label="Kritiske fejl (24h)" value={kritiskeFejl} ikon="▲" farve={kritiskeFejl > 0 ? C.danger : C.success} />
      </div>

      {/* To-kolonne: agent-aktivitet + opgaver/deploys */}
      <div style={{ display: 'grid', gridTemplateColumns: mobil ? '1fr' : '1fr 1fr', gap: 20, marginBottom: 20 }}>
        {/* Agent-aktivitet */}
        <Kort>
          <h3 style={{ margin: '0 0 14px', fontSize: 15, color: C.tekst }}>Seneste agent-aktivitet</h3>
          {agentSamtaler.length === 0 ? (
            <TomTilstand
              titel="Ingen agent-aktivitet"
              besked="Agent-samtaler vises her, når OS-agenter kommunikerer."
              ikon="◑"
              badge="LIVE"
            />
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 6, maxHeight: 300, overflowY: 'auto' }}>
              {agentSamtaler.slice(0, 8).map(k => {
                const kildeFarve = k.kilde === 'hjerne' ? '#3fd4c7' : '#8b5cf6';
                return (
                  <div key={k.id} style={{
                    background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.06)',
                    borderLeft: `3px solid ${kildeFarve}`, borderRadius: 6, padding: '7px 10px', fontSize: 12,
                  }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 3, flexWrap: 'wrap' }}>
                      <span style={{ fontWeight: 700, color: C.tekst }}>{k.fra_agent || '?'}</span>
                      <span style={{ color: C.subtekst }}>→</span>
                      <span style={{ fontWeight: 700, color: C.tekst }}>{k.til_agent || 'alle'}</span>
                      <span style={{ marginLeft: 'auto', fontSize: 10, color: C.subtekst }}>{formatTid(k.oprettet_kl)}</span>
                    </div>
                    <div style={{ color: C.subtekst, lineHeight: 1.4, whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
                      {(k.indhold || '').slice(0, 120)}{(k.indhold || '').length > 120 ? '…' : ''}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </Kort>

        {/* Afventende opgaver + seneste deploys */}
        <Kort>
          <h3 style={{ margin: '0 0 14px', fontSize: 15, color: C.tekst }}>Opgaver & deploys</h3>
          {afventerOpgaver.length === 0 && (
            <div style={{ color: C.success, fontSize: 13, marginBottom: 12 }}>Ingen afventende opgaver</div>
          )}
          {afventerOpgaver.slice(0, 5).map(o => (
            <div key={o.id} style={{
              display: 'flex', alignItems: 'center', gap: 8, padding: '6px 10px', borderRadius: 6,
              background: 'rgba(139,92,246,0.06)', border: '1px solid rgba(139,92,246,0.15)',
              marginBottom: 5, fontSize: 12,
            }}>
              <span style={{ fontSize: 10, fontWeight: 700, color: C.lilla, background: 'rgba(139,92,246,0.15)', borderRadius: 4, padding: '2px 6px', flexShrink: 0 }}>
                {o.afdeling || '?'}
              </span>
              <span style={{ color: C.tekst, flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{o.titel}</span>
            </div>
          ))}
          {deployHistorik?.github_runs?.length > 0 && (
            <>
              <div style={{ fontSize: 11, fontWeight: 700, color: C.subtekst, textTransform: 'uppercase', letterSpacing: 0.8, marginTop: 12, marginBottom: 6 }}>Seneste deploys</div>
              {deployHistorik.github_runs.slice(0, 3).map(run => {
                const concl = run.conclusion;
                const farve = concl === 'success' ? C.success : concl === 'failure' ? C.danger : C.warning;
                return (
                  <div key={run.id} style={{
                    display: 'flex', alignItems: 'center', gap: 8, fontSize: 12,
                    padding: '5px 8px', borderRadius: 5, marginBottom: 4,
                    background: `${farve}10`, border: `1px solid ${farve}20`,
                  }}>
                    <span style={{ fontWeight: 700, color: farve, minWidth: 44, fontSize: 10 }}>
                      {concl === 'success' ? 'OK' : concl === 'failure' ? 'FEJL' : run.status?.toUpperCase() || '?'}
                    </span>
                    <span style={{ color: C.tekst, flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{run.name}</span>
                    <span style={{ fontSize: 10, color: C.subtekst, whiteSpace: 'nowrap' }}>{formatTid(run.opdateret)}</span>
                  </div>
                );
              })}
            </>
          )}
          {(!deployHistorik?.github_runs?.length) && afventerOpgaver.length === 0 && (
            <TomTilstand titel="Ingen aktive opgaver" besked="Systemet er i ro." ikon="✓" badge="LIVE" style={{ marginTop: 8 }} />
          )}
        </Kort>
      </div>

      {/* Cashflow-status + Hjerne-puls */}
      <div style={{ display: 'grid', gridTemplateColumns: mobil ? '1fr' : '1fr 1fr', gap: 20 }}>
        {/* Lønforpligtelse */}
        <Kort>
          <h3 style={{ margin: '0 0 14px', fontSize: 15, color: C.tekst }}>Cashflow-prioritet</h3>
          <div style={{
            background: 'rgba(245,158,11,0.08)', border: '2px solid rgba(245,158,11,0.3)',
            borderRadius: 10, padding: '14px 16px', display: 'flex', alignItems: 'center', gap: 12, marginBottom: 12,
          }}>
            <span style={{ fontSize: 24 }}>◈</span>
            <div>
              <div style={{ fontSize: 12, color: C.warning, fontWeight: 700, marginBottom: 2 }}>FØRSTE PRIORITET</div>
              <div style={{ fontSize: 16, fontWeight: 700, color: C.tekst }}>Lønforpligtelse: 60.000 kr/md</div>
            </div>
            <span style={{
              marginLeft: 'auto', fontSize: 9, fontWeight: 700, padding: '3px 8px', borderRadius: 6,
              background: loenDaekket === true || loenDaekket === 'true'
                ? 'rgba(34,197,94,0.12)' : 'rgba(59,130,246,0.12)',
              border: loenDaekket === true || loenDaekket === 'true'
                ? '1px solid rgba(34,197,94,0.3)' : '1px solid rgba(59,130,246,0.3)',
              color: loenDaekket === true || loenDaekket === 'true' ? '#4ade80' : '#60a5fa',
              textTransform: 'uppercase',
            }}>
              {loenDaekket === true || loenDaekket === 'true' ? 'DÆKKET' : 'KONFIGURERET'}
            </span>
          </div>
          <div style={{ fontSize: 12, color: C.subtekst }}>
            Cashflow-detaljer vises i <button onClick={() => onNavigere && onNavigere('okonomi')} style={{ background: 'none', border: 'none', color: C.sidebarAkt, cursor: 'pointer', fontSize: 12, padding: 0, textDecoration: 'underline' }}>Økonomi</button>
          </div>
        </Kort>

        {/* Hjerne-puls */}
        <Kort>
          <h3 style={{ margin: '0 0 14px', fontSize: 15, color: C.tekst }}>Hjerne-aktivitet</h3>
          {hjernePuls ? (
            <div>
              <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 10 }}>
                <Dot aktiv={hjernePuls.minutter_siden < 60} farve={hjernePuls.minutter_siden < 60 ? C.success : hjernePuls.minutter_siden < 1440 ? C.warning : C.danger} />
                <span style={{ fontSize: 13, color: C.tekst, fontWeight: 600 }}>Seneste viden-skriv</span>
                <span style={{ marginLeft: 'auto', fontSize: 13, fontWeight: 700, color: hjernePuls.minutter_siden < 60 ? C.success : hjernePuls.minutter_siden < 1440 ? C.warning : C.danger }}>
                  {hjernePulsLabel()}
                </span>
              </div>
              <div style={{ fontSize: 11, color: C.subtekst }}>
                {formatTid(hjernePuls.oprettet_kl)}
              </div>
              {hjernePuls.minutter_siden > 1440 && (
                <div style={{ marginTop: 10, padding: '8px 10px', background: 'rgba(239,68,68,0.08)', border: '1px solid rgba(239,68,68,0.2)', borderRadius: 6, fontSize: 12, color: '#fca5a5' }}>
                  Hjernen har ikke vokset i over {Math.round(hjernePuls.minutter_siden / 1440)} dag(e). Systemet kører ikke autonomt. Se CLAUDE.md §5.9.
                </div>
              )}
            </div>
          ) : (
            <TomTilstand
              titel="Hjerne-puls ikke tilgængelig"
              besked="Kræver /api/os/hjerne/viden/seneste endpoint. Implementér i Fase 2."
              ikon="◉"
              badge="IKKE FORBUNDET"
            />
          )}
        </Kort>
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────
// NY SIDEBAR — 5-sektions struktur
// ─────────────────────────────────────────────────────────
function NySidebar({ aktiv, onSkift, onLogout, ulæst = {}, serverVersion }) {
  return (
    <div className="sv-os-sidebar" style={{
      width: 248, minWidth: 248, height: '100vh',
      background: 'linear-gradient(180deg, #050c1a 0%, #060e1e 100%)',
      display: 'flex', flexDirection: 'column',
      position: 'fixed', left: 0, top: 0, zIndex: 100,
      borderRight: '1px solid rgba(63,212,199,0.07)',
    }}>
      {/* Logo */}
      <div style={{ padding: '22px 22px 18px', borderBottom: '1px solid rgba(255,255,255,0.05)' }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8 }}>
          <div style={{ fontSize: 17, fontWeight: 800, color: '#f1f5f9', letterSpacing: -0.3 }}>
            SkilsmisseVejen
          </div>
          {serverVersion && (
            <span style={{
              fontSize: 10, fontWeight: 700,
              background: 'rgba(201,168,76,0.15)', border: '1px solid rgba(201,168,76,0.4)',
              borderRadius: 5, padding: '2px 7px', color: '#c9a84c',
              fontFamily: 'monospace', letterSpacing: '0.03em', flexShrink: 0,
            }}>
              v{serverVersion}
            </span>
          )}
        </div>
        <div style={{ fontSize: 11, color: '#3fd4c7', marginTop: 3, opacity: 0.6, fontFamily: 'monospace' }}>
          OS — Operatørsystem
        </div>
      </div>
      {/* Nav */}
      <nav style={{ flex: 1, padding: '8px 0', overflowY: 'auto' }}>
        {NY_NAV_GROUPS.map(group => (
          <div key={group.label} style={{ marginBottom: 2 }}>
            <div style={{
              padding: '10px 22px 3px',
              fontSize: 9.5, fontWeight: 700, letterSpacing: 1.5,
              color: 'rgba(63,212,199,0.38)', textTransform: 'uppercase',
            }}>
              {group.label}
            </div>
            {group.items.map(item => {
              const erAktiv = aktiv === item.id;
              const badge = ulæst[item.id] || 0;
              return (
                <button
                  key={item.id}
                  onClick={() => onSkift(item.id)}
                  style={{
                    width: '100%', display: 'flex', alignItems: 'center', gap: 10,
                    padding: '9px 22px', border: 'none',
                    background: erAktiv ? 'rgba(63,212,199,0.09)' : 'none',
                    cursor: 'pointer', textAlign: 'left',
                    color: erAktiv ? '#3fd4c7' : '#9db3c8',
                    fontWeight: erAktiv ? 700 : 400, fontSize: 14,
                    borderLeft: `3px solid ${erAktiv ? '#3fd4c7' : 'transparent'}`,
                    transition: 'color .15s, background .15s',
                  }}
                  onMouseEnter={e => {
                    if (!erAktiv) { e.currentTarget.style.color = '#c8d8ee'; e.currentTarget.style.background = 'rgba(255,255,255,0.03)'; }
                  }}
                  onMouseLeave={e => {
                    if (!erAktiv) { e.currentTarget.style.color = '#9db3c8'; e.currentTarget.style.background = 'none'; }
                  }}
                >
                  <span style={{ fontSize: 15, lineHeight: 1, flexShrink: 0 }}>{item.ikon}</span>
                  <span style={{ flex: 1 }}>{item.label}</span>
                  {badge > 0 && (
                    <span style={{ background: '#e74c3c', color: '#fff', borderRadius: 10, fontSize: 10, fontWeight: 700, padding: '1px 6px', flexShrink: 0 }}>
                      {badge}
                    </span>
                  )}
                </button>
              );
            })}
          </div>
        ))}
      </nav>
      {/* Bund */}
      <div style={{ padding: '14px 22px', borderTop: '1px solid rgba(255,255,255,0.05)' }}>
        <div style={{ fontSize: 14, fontWeight: 600, color: '#e2e8f0', marginBottom: 1 }}>René Sørensen</div>
        <div style={{ fontSize: 11, color: '#3fd4c7', opacity: 0.6, marginBottom: 10 }}>os_admin</div>
        <button
          onClick={() => { if (onLogout) onLogout(); else { sessionStorage.removeItem('sv_token_exp'); sessionStorage.removeItem('sv_bruger'); window.location.reload(); } }}
          style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#475569', fontSize: 12, padding: 0 }}
          onMouseEnter={e => e.currentTarget.style.color = C.danger}
          onMouseLeave={e => e.currentTarget.style.color = '#475569'}
        >
          Log ud →
        </button>
      </div>
    </div>
  );
}

// ─── BottomNav til mobil ──────────────────────────────────
// OS-01: Maks 5 primære faner + "Mere"-menu. Oversigt og Indbakke er
// ALTID synlige (de to første pladser). Resterende faner er tilgængelige
// via "Mere"-menuen der åbner en oversigt-liste over alle faner.
// Desktop top-nav er uændret.
// v1.6.81: Hjem-ikon er nu et fast, altid-synligt tab i bundnav (ikke gemt i Mere).
function NyBottomNav({ aktiv, onSkift, onTilbage }) {
  const [mereAaben, setMereAaben] = useState(false);
  const alle = NY_NAV_GROUPS.flatMap(g => g.items);

  // Når onTilbage er tilgængelig: 3 primære faner + Hjem-tab + Mere.
  // Uden onTilbage: 4 primære faner + Mere (som før).
  const PRIMÆRE_IDS = onTilbage
    ? ['oversigt', 'indbakke', 'os_oversigt']
    : ['oversigt', 'indbakke', 'os_oversigt', 'sager'];
  const primære = PRIMÆRE_IDS.map(id => alle.find(i => i.id === id)).filter(Boolean);

  // Alle øvrige faner vises i Mere-menuen
  const sekundære = alle.filter(i => !PRIMÆRE_IDS.includes(i.id));

  // Er den aktive fane en sekundær? Vis den da i bunden i stedet for "Mere"-ikonet
  const aktivErSekundær = sekundære.some(i => i.id === aktiv);
  const aktivItem = alle.find(i => i.id === aktiv);

  // Tab-liste: primære + enten aktiv-sekundær (som ekstra tab) eller ingenting
  const visListe = aktivErSekundær
    ? [...primære, aktivItem]
    : primære;

  const TabKnap = ({ item, erAktiv, onClick }) => (
    <button onClick={onClick} style={{
      flex: '1 1 0', display: 'flex', flexDirection: 'column',
      alignItems: 'center', gap: 2, padding: '8px 4px',
      background: 'none', border: 'none', cursor: 'pointer',
      color: erAktiv ? C.sidebarAkt : C.sidebarTxt,
      fontSize: 10, fontWeight: erAktiv ? 700 : 400,
      borderTop: erAktiv ? `2px solid ${C.sidebarAkt}` : '2px solid transparent',
      minWidth: 0,
    }}>
      <span style={{ fontSize: 18, lineHeight: 1 }}>{item.ikon}</span>
      <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', maxWidth: '100%' }}>
        {item.label}
      </span>
    </button>
  );

  return (
    <>
      {/* Mere-menu overlay */}
      {mereAaben && (
        <div
          onClick={() => setMereAaben(false)}
          style={{
            position: 'fixed', inset: 0, zIndex: 199,
            background: 'rgba(0,0,0,0.5)',
          }}
        >
          <div
            onClick={e => e.stopPropagation()}
            style={{
              position: 'fixed', bottom: 56, left: 0, right: 0, zIndex: 200,
              background: C.sidebar, borderTop: '1px solid rgba(255,255,255,.15)',
              borderRadius: '16px 16px 0 0',
              padding: '12px 0 8px',
              maxHeight: '50vh', overflowY: 'auto',
            }}
          >
            <div style={{ fontSize: 11, color: C.sidebarTxt, textTransform: 'uppercase', letterSpacing: 1, padding: '0 16px 8px', fontWeight: 700, opacity: 0.6 }}>
              Alle sektioner
            </div>
            {NY_NAV_GROUPS.map(group => (
              <div key={group.label}>
                <div style={{ fontSize: 10, color: C.sidebarTxt, textTransform: 'uppercase', letterSpacing: 1, padding: '6px 16px 2px', opacity: 0.4 }}>
                  {group.label}
                </div>
                {group.items.map(item => (
                  <button
                    key={item.id}
                    onClick={() => { onSkift(item.id); setMereAaben(false); }}
                    style={{
                      width: '100%', display: 'flex', alignItems: 'center', gap: 12,
                      padding: '10px 16px', border: 'none',
                      background: aktiv === item.id ? 'rgba(63,212,199,0.1)' : 'none',
                      cursor: 'pointer', textAlign: 'left',
                      color: aktiv === item.id ? C.sidebarAkt : C.sidebarTxt,
                      fontSize: 13, fontWeight: aktiv === item.id ? 700 : 400,
                    }}
                  >
                    <span style={{ fontSize: 16 }}>{item.ikon}</span>
                    {item.label}
                  </button>
                ))}
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Bundnav */}
      <div className="sv-os-bottomnav" style={{
        position: 'fixed', bottom: 0, left: 0, right: 0, zIndex: 200,
        background: C.sidebar, borderTop: '1px solid rgba(255,255,255,.1)',
        display: 'flex', paddingBottom: 'env(safe-area-inset-bottom)',
      }}>
        {visListe.map(item => (
          <TabKnap
            key={item.id}
            item={item}
            erAktiv={aktiv === item.id}
            onClick={() => { onSkift(item.id); setMereAaben(false); }}
          />
        ))}
        {/* Hjem-tab — fast, altid synlig, navigerer til sagsportalen (v1.6.81) */}
        {onTilbage && (
          <button
            onClick={onTilbage}
            aria-label="Tilbage til Sagsportalen"
            title="Tilbage til Sagsportalen"
            style={{
              flex: '1 1 0', display: 'flex', flexDirection: 'column',
              alignItems: 'center', gap: 2, padding: '8px 4px',
              background: 'none', border: 'none', cursor: 'pointer',
              color: C.sidebarAkt,
              fontSize: 10, fontWeight: 600,
              borderTop: `2px solid ${C.sidebarAkt}`,
              minWidth: 0,
            }}
          >
            <Home size={18} strokeWidth={2} />
            <span>Hjem</span>
          </button>
        )}
        {/* "Mere"-knap som sidste tab — vises altid */}
        <button
          onClick={() => setMereAaben(m => !m)}
          style={{
            flex: '1 1 0', display: 'flex', flexDirection: 'column',
            alignItems: 'center', gap: 2, padding: '8px 4px',
            background: 'none', border: 'none', cursor: 'pointer',
            color: (mereAaben || aktivErSekundær) ? C.sidebarAkt : C.sidebarTxt,
            fontSize: 10, fontWeight: 400,
            borderTop: mereAaben ? `2px solid ${C.sidebarAkt}` : '2px solid transparent',
            minWidth: 0,
          }}
        >
          <span style={{ fontSize: 18, lineHeight: 1 }}>⋯</span>
          <span>Mere</span>
        </button>
      </div>
    </>
  );
}

// ─────────────────────────────────────────────────────────
// HOVED-KOMPONENT: OSDashboardNy
// ─────────────────────────────────────────────────────────
// Importeres inline fra OSDashboard.jsx når flag er ON.
// Den gamle OSDashboard slettes IKKE i Fase 1.
const DEFAULT_SEKTION_NY = 'oversigt';

export default function OSDashboardNy({ onTilbage, bruger, onLogout } = {}) {
  const [aktivSektion, setAktivSektion] = useState(DEFAULT_SEKTION_NY);
  const [ulæstIndbakke, setUlæstIndbakke] = useState(0);
  const [serverVersion, setServerVersion] = useState(null);
  const mobil = useMobile();

  // Hent ulæst indbakke-tæller
  useEffect(() => {
    function hentUlæst() {
      fetch('/api/os/indbakke', { credentials: 'include' })
        .then(r => r.ok ? r.json() : { beskeder: [] })
        .then(d => setUlæstIndbakke((d.beskeder || []).filter(b => !b.laest).length))
        .catch(() => {});
    }
    hentUlæst();
    const id = setInterval(hentUlæst, 60000);
    return () => clearInterval(id);
  }, []);

  // Hent server-version
  useEffect(() => {
    fetch('/api/health')
      .then(r => r.json())
      .then(d => { if (d?.version) setServerVersion(d.version); })
      .catch(() => {});
  }, []);

  // Bestem hvilken komponent der vises.
  // Hvert panel er indpakket i SubBoundary — så et panel-crash isoleres
  // til netop det panel og smitter ikke hele dashboardet (SubBoundary
  // auto-resetter desuden efter 5 sek ved forbigående fejl).
  const renderDashboard = () => {
    if (aktivSektion === 'oversigt') {
      return (
        <SubBoundary navn="Oversigt">
          <OSCockpit onNavigerTilFane={setAktivSektion} />
        </SubBoundary>
      );
    }
    if (DASHBOARD_MAP[aktivSektion]) {
      const Komp = DASHBOARD_MAP[aktivSektion];
      return (
        <SubBoundary navn={aktivSektion}>
          <Komp key={aktivSektion} onNavigate={setAktivSektion} />
        </SubBoundary>
      );
    }
    return (
      <TomTilstand
        titel={`${aktivSektion} — Ikke implementeret`}
        besked="Denne fane er planlagt til en kommende fase."
        ikon="◻"
        badge="IKKE FORBUNDET"
      />
    );
  };

  return (
    <MobilCtx.Provider value={mobil}>
      <div style={{
        minHeight: '100vh',
        background: 'linear-gradient(135deg, #060917 0%, #0a0f22 50%, #070e1c 100%)',
        fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif',
      }}>
        {/* Top-bjælke (Fase A — erstatter sidebar) */}
        {!mobil && (
          <TopBjaelke
            aktivSektion={aktivSektion}
            onNavigere={setAktivSektion}
            onLogout={onLogout}
            onTilbage={onTilbage}
            serverVersion={serverVersion}
            ulæstIndbakke={ulæstIndbakke}
          />
        )}

        {/* Hoved-indhold — fuld bredde, paddingTop = bjælkehøjde 48px */}
        <main className="sv-os-main" style={{
          paddingTop: mobil ? 0 : 48,
          padding: mobil
            ? '16px 16px 80px'
            : '48px 44px 40px',
          minHeight: '100vh',
          overflowY: 'auto',
          boxSizing: 'border-box',
        }}>
          {renderDashboard()}
        </main>

        {/* Mobil: behold BottomNav */}
        {mobil && <NyBottomNav aktiv={aktivSektion} onSkift={setAktivSektion} onTilbage={onTilbage} />}

      </div>
    </MobilCtx.Provider>
  );
}

// LegacyOSDashboard, LegacyOsOversigt og LegacyWrapper fjernet i Fase 3
// (os_oversigt → OsOversigt2Panel, scheduler → SchedulerPanel)
